import * as React from 'react';
import { useRef, useEffect, useState } from 'react';

import { Grid } from '@mui/material';
import { DirectionsCarFilledOutlined, GamepadOutlined, SlideshowOutlined, SportsEsportsOutlined, PanToolOutlined } from '@mui/icons-material';

import ItemCard from './ItemCard';
import ItemCardFullWidth from './ItemCardFullWidth';
import { Car } from '../ModelView/geometries/Car';
import { Gamepad } from '../ModelView/geometries/Gamepad';
import { LeftHand } from '../ModelView/geometries/LeftHand';
import { Obstacles } from '../ModelView/geometries/Obstacles';
import { RightHand } from '../ModelView/geometries/RightHand';

import './ProjectPageStyles.css';
import '../ContentPagesStyles.css';

export interface IProjectPageProps{

}

const ProjectPage = (props: IProjectPageProps) => {
    const projectDescription = ["Die Projektgruppe „Error 404: Physics not found“ hatte sich zum Ziel gesetzt die Mensch-Computer Interaktion am Beispiel eines Gamecontrollers zu untersuchen. Hierzu wurde das Open-Source Physiksimulationsprogramm MuJoCo verwendet. Das entstandene Modell ermöglicht die Simulation komplexer Hand- und Fingerbewegungen, sowie die Erfassung sämtlicher Eingaben am Controller, wodurch ein Auto gesteuert wird. Ausgehend von diesem Modell wurde Beispielsweise die optimale Dämpfung eines „thumb-sticks“ mithilfe des Python Moduls scipy.optimize ermittelt."];
    const handDescription = ["Die Handmodelle wurden jeweils auf Basis der „.stl mesh files“ des „Modular Prosthetic Limb“ der Johns Hopkins University erstellt. Hierbei handelt es sich um eine detailgetreue Abbildung der menschlichen Hand und ihrer Funktionalität. Die MPL ist daher die weltweit fortschrittlichste Prothese und wird überwiegend in der Neurorehabilitationsforschung genutzt. Das verwendete Modell besitzt 22 Joints, mit denen nahezu alle Bewegungen einer realen Hand nachgeahmt werden können."];
    const controllerDescription =  ["Das Controllermodell orientiert sich am Aussehen als auch in seiner Einfachheit am 8BitDo SF30 PRO. Einem Retro-Controller bestehend aus zwei Joysticks, vier Buttons und einem D-Pad auf der linken Seite. In Bezug auf die Steuerung des Autos wurde folgende Belegung gewählt:","- Linker Stick für Beschleunigen und Bremsen","- Rechter Stick für Richtungswechsel","- Blauer und grüner Button für das Ein- und Ausschalten der Frontscheinwerfer"];
    const carDescription = ["Das Modellauto verfügt über eine Zahnstangenlenkung, die bei einem Lenkbefehl das Ritzel dreht, sodass zunächst die Zahnstange und daraufhin dessen Spurstangen verschoben werden. Die Antriebsart ist ein klassischer Allrad mit einer gleichmäßigen Verteilung der Antriebskraft. Des Weiteren besitzt das Auto zwei Frontscheinwerfer, die durch einen Knopfdruck am Controller ein – und ausgeschaltet werden können."];

    const [minCardHeight, setMinCardHeight] = useState<number>(0);

    let gamepadSingleRef = useRef<any>();
    
    let projectDescRef = useRef<any>();

    useEffect(() => {
        setCardHeight();

        window.addEventListener("resize", setCardHeight);

        return () => {
            window.removeEventListener("resize", () => {});
        };
    },[])

    const setCardHeight = () => {
        const cardHeight = projectDescRef.current.getContentMinHeight();
    
        setMinCardHeight(cardHeight);
    }

    return(
        <Grid className="content-page-content" container direction="row" justifyContent="center">
            <Grid className="content-page-header" item>
                <span className="header-3dtext">Das Projekt</span>
            </Grid>
            <Grid className="content-page-divider-div" item>
                <div className="content-page-divider"/>
            </Grid>
            <Grid className="project-page-item page-item-left" item>
                <ItemCard
                    ref={projectDescRef}
                    cameraPos={[0, 2 , 2]}
                    canvasId={1}
                    cardContent={projectDescription}
                    headerAvatar={<SportsEsportsOutlined />}
                    headerLabel="Unser Projekt"
                    orientation='left'
                    models={
                        [
                            <Gamepad pos={[-0.09, -0.30, 0.15]} />,
                            <Obstacles pos={[-1.58, -2.2, 0]} euler={[0, 0, 1.57]} />,
                            <Car pos={[0, 0, 0.065]} />,
                            <LeftHand pos={[-0.1535, -0.35, 0.18]} euler={[0, -1.5, 3.14]} />,
                            <RightHand pos={[0.104, -0.35, 0.18]} euler={[0, 1.5, 3.14]} />
                        ]
                    }
                    showMujocoGround
                    minHeight={minCardHeight}
                />
            </Grid>
            <Grid className="project-page-item page-item-right" item>
                <ItemCard
                    cameraPos={[-0.02475, 0.4 , 0.18]}
                    cameraTargetPos={[0, 0, 0]}
                    canvasId={2}
                    cardContent={handDescription}
                    headerAvatar={<PanToolOutlined />}
                    headerLabel="Die Hände"
                    orientation='right'
                    models={
                        [
                            <LeftHand pos={[-0.12875, 0, 0]} euler={[0, -1.5, 3.14]} />,
                            <RightHand pos={[0.12875, 0, 0]} euler={[0, 1.5, 3.14]} />
                        ]
                    }
                    minHeight={minCardHeight}
                />
            </Grid>
            <Grid className="project-page-item page-item-left" item>
                <ItemCard
                    cameraTargetPos={[0.0725, 0, 0.020625]}
                    cameraPos={[0, 0.2, 0.2]}
                    canvasId={3}
                    cardContent={controllerDescription}
                    headerAvatar={<GamepadOutlined />}
                    headerLabel="Der (Retro-) Controller"
                    orientation='left'
                    maxZoomDistance={0.8}
                    models={
                        [
                            <Gamepad ref={gamepadSingleRef} pos={[0, 0, 0]} />,
                        ]
                    }
                    modelRef={gamepadSingleRef}
                    minHeight={minCardHeight}
                />
            </Grid>
            <Grid className="project-page-item page-item-right" item>
                <ItemCard
                    cameraPos={[1, 1, 0]}
                    cameraTargetPos={[0, 0, 0]}
                    canvasId={4}
                    cardContent={carDescription}
                    headerAvatar={<DirectionsCarFilledOutlined />}
                    headerLabel="Der (Sport-) Wagen"
                    orientation='right'
                    maxZoomDistance={0.6}
                    models={
                        [
                            <Car pos={[0, 0, 0]} />
                        ]
                    }
                    minHeight={minCardHeight}
                />
            </Grid>
            <Grid className='project-page-item page-item-center' item>
                <ItemCardFullWidth headerAvatar={<SlideshowOutlined />} headerLabel="Präsentationsunterlagen" />
            </Grid>
        </Grid>
    );
}

export default ProjectPage;